import { type ElementType, Fragment, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router'

import { Paths } from '~/App'
import Layout, { LayoutContent, LayoutFooter } from '~/App/components/Layout'
import { LayoutLoadingContent } from '~/App/components/LoadingLayout'
import RateInput from '~/App/components/RateInput'
import StoreLayoutHeader from '~/App/Store/components/StoreLayoutHeader'
import useCloseStore from '~/App/Store/hooks/useCloseStore'
import type { SurveyQuestion, SurveyResponse } from '~/App/Surveys/@types'
import useSaveSurvey from '~/App/Surveys/hooks/useSaveSurvey'
import useSurveyQuestions from '~/App/Surveys/hooks/useSurveyQuestions'
import { Button } from '~/DesignSystem/components/button'
import { Card, CardContent } from '~/DesignSystem/components/card'
import { Label } from '~/DesignSystem/components/label'
import { Separator } from '~/DesignSystem/components/separator'
import { toast } from '~/DesignSystem/components/sonner'
import { Textarea as TextareaComponent } from '~/DesignSystem/components/textarea'
import { cn } from '~/DesignSystem/lib/utils'

const Textarea = ({
  className,
  ...props
}: React.ComponentProps<typeof TextareaComponent> & SurveyQuestion) => {
  return (
    <TextareaComponent
      maxLength={props.type === 'textarea' ? props.max_length : undefined}
      className={cn('min-h-60', className)}
      {...props}
    />
  )
}

const InputsMap: Record<SurveyQuestion['type'], ElementType> = {
  rating: RateInput,
  textarea: Textarea,
}

type QuestionsFormData = Record<string, string | number | undefined>

const SurveyPage = () => {
  const intl = useIntl()
  const {
    data: questions,
    isFetching: loadingQuestions,
    error: questionsError,
    isSuccess: loadedQuestions,
  } = useSurveyQuestions()

  const { mutateAsync: saveSurvey, isPending: savingSurvey } = useSaveSurvey()

  const navigate = useNavigate()
  const close = useCloseStore()

  const steps = useMemo(() => {
    return questions?.attributes.items.reduce<number[]>((acc, item) => {
      if (!acc.includes(item.step)) {
        acc.push(item.step)
      }
      return acc
    }, [])
  }, [questions])

  const [step, setStep] = useState<NonNullable<typeof steps>[number]>()

  const { currentStep, prevStep, nextStep } = useMemo(() => {
    if (!steps) return {}
    const currentStep = step ?? steps[0]

    return {
      currentStep,
      prevStep: steps[steps.indexOf(currentStep) - 1],
      nextStep: steps[steps.indexOf(currentStep) + 1],
    }
  }, [steps, step])

  if (questionsError) {
    throw questionsError
  }

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<QuestionsFormData>({ mode: 'onChange' })

  const goToBack = () => {
    if (prevStep !== undefined) {
      return setStep(prevStep)
    }
    close()
  }

  const submit = async (data: QuestionsFormData) => {
    if (nextStep !== undefined) {
      return setStep(nextStep)
    }

    try {
      const reponses = Object.keys(data).reduce<SurveyResponse[]>((collector, key) => {
        if (data[key]) {
          collector.push({ id: key, attributes: { value: data[key] } })
        }
        return collector
      }, [])

      await saveSurvey(reponses)

      navigate(Paths.surveySuccess, {
        replace: true,
      })
    } catch {
      toast.error(intl.formatMessage({ id: 'errors.request' }))
    }
  }

  return (
    <Layout>
      <StoreLayoutHeader />

      {loadingQuestions && <LayoutLoadingContent />}

      {loadedQuestions && (
        <>
          <LayoutContent className="flex items-center justify-center">
            <form id="survey" onSubmit={handleSubmit(submit)}>
              <Card className="w-full max-w-md">
                {questions.attributes.items.map(item => {
                  const Input = InputsMap[item.type]
                  if (!Input) throw new Error(`No input for type ${item.type}`)

                  if (item.step !== currentStep) return null

                  return (
                    <Fragment key={item.id}>
                      <CardContent className="text-center">
                        <Label htmlFor={`${item.id}`} className="block text-base font-normal mb-5">
                          {item.label}
                          {item.required === false && (
                            <>
                              {' '}
                              <span className="text-muted-foreground">
                                (<FormattedMessage id="label.optional" />)
                              </span>
                            </>
                          )}
                        </Label>
                        <Controller
                          name={`${item.id}`}
                          control={control}
                          defaultValue=""
                          rules={{ required: item.required }}
                          render={({ field }) => <Input {...item} {...field} />}
                        />
                      </CardContent>
                      <Separator className="last:hidden" />
                    </Fragment>
                  )
                })}
              </Card>
            </form>
          </LayoutContent>
          <LayoutFooter className="flex flex-row w-full gap-4">
            <Button
              onClick={goToBack}
              variant="outline"
              size="xl"
              type="button"
              className="flex-1"
              disabled={savingSurvey}
            >
              <FormattedMessage id="actions.goback" />
            </Button>
            <Button
              type="submit"
              form="survey"
              disabled={!isValid || savingSurvey}
              size="xl"
              className="flex-1"
            >
              {nextStep ? (
                <FormattedMessage id="actions.next" />
              ) : (
                <FormattedMessage id="actions.send" />
              )}
            </Button>
          </LayoutFooter>
        </>
      )}
    </Layout>
  )
}

export default SurveyPage
